import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { ExcelExportButton } from '../../components/ui/ExcelExportButton';
import { SortableTh } from '../../components/ui/SortableTh';
import { ApiError } from '../../lib/api-client';
import { confirmAction } from '../../lib/confirm-service';
import { exportRowsToExcel, fetchAllPages } from '../../lib/excel-export';
import { useTableSort } from '../../lib/table-sort';
import { useToast } from '../../lib/toast';
import { fetchBillingList } from './billing.api';
import { useApproveBillingRow, useBillingList } from './billing.queries';
import type { BillingListFilters, BillingModule, BillingRow } from './types';

const moduleAccent: Record<BillingModule, string> = {
  opd: 'accent-violet',
  emg: 'accent-rose',
  'ipd-daycare': 'accent-indigo',
  dialysis: 'accent-teal',
  investigation: 'accent-cyan',
};

const moduleFilters: { module: BillingModule; label: string }[] = [
  { module: 'opd', label: 'OPD' },
  { module: 'emg', label: 'EMG' },
  { module: 'ipd-daycare', label: 'IPD/Daycare' },
  { module: 'dialysis', label: 'Dialysis' },
  { module: 'investigation', label: 'Investigation' },
];

const billDetailHref = (row: BillingRow) => {
  switch (row.module) {
    case 'opd': return `/opd/${row.id}`;
    case 'emg': return `/emg/${row.id}`;
    case 'investigation': return `/investigation-bill/${row.id}`;
    case 'ipd-daycare': return row.hasBilling ? `/ipd-daycare/${row.id}?tab=bill-details` : `/ipd-daycare/${row.id}`;
    case 'dialysis': return `/dialysis/${row.id}`;
  }
};

const currency = (value: number | null) => value === null ? '—' : new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(value);
const billDateFormatter = new Intl.DateTimeFormat('en-US', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata',
});
const formatBillDate = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const parts = billDateFormatter.formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? '';
  return `${get('day')}-${get('month')}-${get('year')} ${get('hour')}:${get('minute')} ${get('dayPeriod').toUpperCase()}`;
};

const initialFilters = (): BillingListFilters => ({ page: 1, limit: 25, approvalStatus: 'pending' });

const BillingActionCell = ({ row, onApprove, pending }: { row: BillingRow; onApprove: (row: BillingRow) => void; pending: boolean }) => {
  if (!row.hasBilling) return <span className="billing-no-billing" title="Billing capture is not part of this module yet">—</span>;
  if (row.approved) return <span className="status-badge active">Approved</span>;
  return <button className="button button-primary billing-approve-button" type="button" disabled={pending} onClick={() => onApprove(row)}>{pending ? 'Approving…' : 'Approve'}</button>;
};

export const BillingListPage = () => {
  const [filters, setFilters] = useState<BillingListFilters>(initialFilters);
  const [searchInput, setSearchInput] = useState('');
  const query = useBillingList(filters);
  const tableSort = useTableSort(filters.sortBy ?? null, filters.sortOrder === 'desc' ? 'desc' : 'asc', (next) => {
    setFilters((current) => ({ ...current, sortBy: next.key, sortOrder: next.direction, page: 1 }));
  });
  const approveMutation = useApproveBillingRow();
  const { showToast } = useToast();

  const applySearch = (event: FormEvent) => {
    event.preventDefault();
    setFilters((current) => ({ ...current, value: searchInput.trim(), page: 1 }));
  };

  const changeLimit = (limit: number) => setFilters((current) => ({ ...current, limit, page: 1 }));
  const changeModule = (module?: BillingModule) => setFilters((current) => {
    const next = { ...current, page: 1 };
    if (module) next.module = module;
    else delete next.module;
    return next;
  });
  const approve = (row: BillingRow) => {
    void confirmAction({
      title: 'Confirm Bill Approval',
      description: `Are you sure you want to approve the ${row.moduleLabel} bill ${row.billId} for ${row.patientName}?`,
      confirmLabel: 'Yes, Approve',
    }).then((confirmed) => {
      if (!confirmed) return;
      approveMutation.mutate({ module: row.module, id: row.id }, {
        onSuccess: () => showToast('Bill approved successfully.', 'success'),
        onError: (error) => showToast(error instanceof ApiError ? error.message : 'The bill could not be approved.', 'error'),
      });
    });
  };
  const isPending = (row: BillingRow) => approveMutation.isPending && approveMutation.variables?.id === row.id;

  const exportToExcel = async () => {
    const rows = await fetchAllPages<BillingRow>((page, limit) => fetchBillingList({ ...filters, page, limit }), 100);
    await exportRowsToExcel({
      fileName: `Billing-List-${new Date().toISOString().slice(0, 10)}`,
      title: 'Bill Approval',
      subtitle: 'Bills raised across OPD, EMG, IPD/Daycare, Dialysis, and Investigation',
      columns: [
        { header: '#', width: 6, align: 'right', map: (_row, index) => index + 1 },
        { header: 'Bill ID', width: 14, map: (row) => row.billId },
        { header: 'Section', width: 14, map: (row) => row.moduleLabel },
        { header: 'Bill Date', width: 22, map: (row) => formatBillDate(row.billDate) },
        { header: 'Patient Name', width: 24, map: (row) => row.uhid ? `${row.patientName} (${row.uhid})` : row.patientName },
        { header: 'Total', width: 14, align: 'right', numFmt: '"₹"#,##0.00', map: (row) => row.total ?? 0 },
        { header: 'Discount', width: 14, align: 'right', numFmt: '"₹"#,##0.00', map: (row) => row.discount ?? 0 },
        { header: 'Grand Total', width: 14, align: 'right', numFmt: '"₹"#,##0.00', map: (row) => row.grandTotal ?? 0 },
        { header: 'Payment', width: 14, align: 'right', numFmt: '"₹"#,##0.00', map: (row) => row.payment ?? 0 },
        { header: 'Due', width: 14, align: 'right', numFmt: '"₹"#,##0.00', map: (row) => row.due ?? 0 },
        { header: 'Created By', width: 18, map: (row) => row.createdBy || '-' },
        { header: 'Approved', width: 11, map: (row) => row.hasBilling ? (row.approved ? 'Yes' : 'No') : 'N/A' },
      ],
      rows,
    });
  };

  const errorMessage = query.error instanceof ApiError ? query.error.message : 'Billing records could not be loaded.';

  return (
    <div className="page-stack billing-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">Billing</p>
          <h1>Bill Approval</h1>
          <p>Review pending bills raised across OPD, EMG, IPD/Daycare, and Investigation.</p>
        </div>
        <div className="billing-quick-links">
          <button type="button" className={`billing-quick-link billing-filter-all${filters.module ? '' : ' is-active'}`} aria-pressed={!filters.module} onClick={() => changeModule()}>
            All
          </button>
          {moduleFilters.map((filter) => (
            <button key={filter.module} type="button" className={`billing-quick-link ${moduleAccent[filter.module]}${filters.module === filter.module ? ' is-active' : ''}`} aria-pressed={filters.module === filter.module} onClick={() => changeModule(filter.module)}>
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      <section className="content-card billing-card">
        <div className="table-toolbar">
          <label className="billing-limit-select">
            Show
            <select value={filters.limit} disabled={query.isFetching} onChange={(event) => changeLimit(Number(event.target.value))}>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            entries
          </label>
          <div className="table-toolbar-actions">
            <form className="search-box" onSubmit={applySearch} role="search">
              <span aria-hidden="true">⌕</span>
              <input value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search patient name, UHID or phone" aria-label="Search bills" />
              <button type="submit">Search</button>
            </form>
            <ExcelExportButton onExport={exportToExcel} disabled={!query.isSuccess} />
          </div>
        </div>

        {query.isLoading ? <LoadingState /> : null}
        {query.isError ? <ErrorState title="Unable to load billing records" message={errorMessage} action={{ label: 'Try again', onClick: () => void query.refetch() }} /> : null}
        {query.isSuccess && query.data.data.length === 0 ? (
          <EmptyState
            title={filters.value ? 'No matching bills' : 'No bills found'}
            message={filters.value ? 'Try a different name, UHID, or phone number.' : 'Pending bills will appear here until they are approved.'}
            {...(filters.value ? { action: { label: 'Clear search', onClick: () => { setSearchInput(''); setFilters((current) => ({ ...current, value: '', page: 1 })); } } } : {})}
          />
        ) : null}
        {query.isSuccess && query.data.data.length > 0 ? (
          <>
            <div className="table-scroll">
              <table className="billing-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <SortableTh control={tableSort} column="billId">Bill ID</SortableTh>
                    <SortableTh control={tableSort} column="billDate">Bill Date</SortableTh>
                    <SortableTh control={tableSort} column="patient">Patient (UHID)</SortableTh>
                    <SortableTh control={tableSort} column="total">Total</SortableTh>
                    <SortableTh control={tableSort} column="discount">Discount</SortableTh>
                    <SortableTh control={tableSort} column="grandTotal">Grand Total</SortableTh>
                    <SortableTh control={tableSort} column="payment">Payment</SortableTh>
                    <SortableTh control={tableSort} column="due">Due</SortableTh>
                    <SortableTh control={tableSort} column="createdBy">Created By</SortableTh>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {query.data.data.map((row, index) => (
                    <tr key={`${row.module}-${row.id}`} className={row.hasBilling && row.approved ? 'billing-row-approved' : undefined}>
                      <td>{(filters.page - 1) * filters.limit + index + 1}</td>
                      <td><Link className="bill-id-link" to={billDetailHref(row)} title="View full bill details"><strong>{row.billId}</strong></Link><span className={`billing-module-badge ${moduleAccent[row.module]}`}>{row.moduleLabel}</span></td>
                      <td>{formatBillDate(row.billDate)}</td>
                      <td>
                        {row.patientId
                          ? <Link className="patient-name-link" to={`/patients/${row.patientId}`} title="Open patient profile"><strong>{row.patientName}</strong></Link>
                          : <strong>{row.patientName}</strong>}
                        {row.uhid ? <small> ({row.uhid})</small> : null}
                      </td>
                      <td>{currency(row.total)}</td>
                      <td>{currency(row.discount)}</td>
                      <td>{currency(row.grandTotal)}</td>
                      <td>{currency(row.payment)}</td>
                      <td>{currency(row.due)}</td>
                      <td>{row.createdBy || '—'}</td>
                      <td><BillingActionCell row={row} onApprove={approve} pending={isPending(row)} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="pagination">
              <button className="button button-secondary" disabled={query.isFetching || filters.page <= 1} onClick={() => setFilters((current) => ({ ...current, page: current.page - 1 }))}>Previous</button>
              <span>Page {filters.page} of {Math.max(query.data.pagination.totalPages, 1)}</span>
              <button className="button button-secondary" disabled={query.isFetching || filters.page >= query.data.pagination.totalPages} onClick={() => setFilters((current) => ({ ...current, page: current.page + 1 }))}>Next</button>
            </div>
          </>
        ) : null}
      </section>
    </div>
  );
};
